<template>
  <RygenButton
    v-if="isEditable"
    mode="tertiary"
    icon="pi pi-pencil"
    icon-pos="left"
    :aria-label="
      t('common.editParam', {
        param: isContractRate ? t('rateShop.quotes') : t('rateShop.spotQuotes'),
      })
    "
    :disabled="disabled"
    :label="rateSourceText"
    @click="onEdit"
  />
  <span v-else class="rate-response-table-source" v-text="rateSourceText" />
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue';
import { computed, inject, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';

import {
  Order,
  OrderStatus,
  RateQuote,
  Shipment,
  ShipmentStatus,
  WaterfallTenderRateQuote,
} from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';
import { isStatusEqualOrBefore } from '@/util';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';

const props = defineProps({
  rateQuote: {
    type: RateQuote,
    default: () => new RateQuote(),
  },
  waterfallTenderRateQuote: {
    type: Object as PropType<WaterfallTenderRateQuote>,
    default: () => undefined,
  },
  canEditRate: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { rateQuote, canEditRate, disabled, waterfallTenderRateQuote } =
  toRefs(props);
const emit = defineEmits<{
  (event: 'edit-brokerage-quote', brokerageQuote: RateQuote): void;
  (event: 'edit-spot-quote', spotQuote: RateQuote): void;
  (event: 'edit-quote', rateQuote: RateQuote): void;
}>();

const { t } = useI18n();
const { parentEntity } = useParentEntityStore();
const canUserEditRateAfterTendering = inject(
  'canUserEditRateAfterTendering',
  ref(false),
) as Ref<boolean>;

const isBrokerage = computed(() => rateQuote.value.source === 'BROKERAGE');
const isContractRate = computed(() => rateQuote.value.source === 'CONTRACT');

const canEditCarrierRate = computed(() =>
  authorization.hasAuthority('rateShop', 'editCarrierRate'),
);

const canEditWaterfallTenderRateQuote = computed(() => {
  if (!waterfallTenderRateQuote?.value) {
    return false;
  }

  const isAlwaysEditable =
    waterfallTenderRateQuote.value.isPending ||
    waterfallTenderRateQuote.value.isTenderFailed;

  const isCurrentlyEditable =
    canUserEditRateAfterTendering.value &&
    (waterfallTenderRateQuote.value.isTendered ||
      waterfallTenderRateQuote.value.isCompleted);

  return isAlwaysEditable || isCurrentlyEditable;
});

const isEditable = computed(() => {
  if (!canEditRate.value) {
    return false;
  }

  if (parentEntity.value instanceof Shipment) {
    const isEditableStatus = isStatusEqualOrBefore(
      parentEntity.value.status,
      ShipmentStatus.CARRIER_ASSIGNED,
    );

    const isEditable =
      isEditableStatus ||
      canUserEditRateAfterTendering.value ||
      canEditWaterfallTenderRateQuote.value;

    return isEditable && canEditCarrierRate.value;
  } else if (parentEntity.value instanceof Order) {
    const isEditableStatus = isStatusEqualOrBefore(
      parentEntity.value.status,
      OrderStatus.PENDING,
    );

    return isEditableStatus && canEditCarrierRate.value;
  } else {
    return false;
  }
});

const rateSourceText = computed(() => {
  return rateQuote.value.isCapacityProvider
    ? t('rateShop.capacityProvider')
    : t(`rateShop.source.${rateQuote.value.source}`);
});

const onEdit = () => {
  if (isBrokerage.value) {
    emit('edit-brokerage-quote', rateQuote.value);
  } else if (isContractRate.value) {
    emit('edit-quote', rateQuote.value);
  } else {
    emit('edit-spot-quote', rateQuote.value);
  }
};
</script>
